import React from 'react';
import { Check, ChevronDown, Lock } from 'lucide-react';
import { Panel } from './ui/panel';

export interface SectionSidebarItem {
  id: string;
  label: React.ReactNode;
  innerNav: React.ReactNode;
  disabled?: boolean;
  locked?: boolean;
  completed?: boolean;
  supportText?: React.ReactNode;
}

interface SectionSidebarProps {
  sections: SectionSidebarItem[];
  selectedSectionId: string | null;
  onSectionSelect?: (id: string) => void;
  deemphasizeInactive?: boolean;
  className?: string;
  header?: React.ReactNode;
  width?: number | string;
}

const DEFAULT_PANEL_WIDTH = 400;
const MIN_EXPANDED_SECTION_HEIGHT = 220;

export const SectionSidebar: React.FC<SectionSidebarProps> = ({
  sections,
  selectedSectionId,
  onSectionSelect,
  deemphasizeInactive = false,
  className,
  header,
  width = DEFAULT_PANEL_WIDTH,
}) => {
  const resolvedWidth = typeof width === 'number' ? `${width}px` : width;

  const renderSectionTrigger = (
    section: SectionSidebarItem,
    isSelected: boolean,
    inlineSupportText?: React.ReactNode,
  ) => {
    const isInteractive = !section.disabled && !section.locked;
    const isDimmed = deemphasizeInactive && !isSelected && !section.locked;

    return (
      <button
        type="button"
        onClick={() => {
          if (isInteractive) {
            onSectionSelect?.(section.id);
          }
        }}
        disabled={!isInteractive}
        aria-pressed={isSelected}
        aria-expanded={isSelected}
        className="tw-w-full tw-text-left tw-rounded-lg tw-transition-all tw-duration-200 tw-flex tw-items-center tw-justify-between"
        style={{
          padding: 'var(--spacing-2) var(--spacing-3)',
          border: isSelected ? '1px solid var(--primary)' : '1px solid var(--border)',
          borderBottomLeftRadius: isSelected ? '0px' : 'var(--radius)',
          borderBottomRightRadius: isSelected ? '0px' : 'var(--radius)',
          background: isSelected ? 'color-mix(in oklab, var(--primary) 8%, var(--card))' : 'transparent',
          color: isSelected ? 'var(--foreground)' : section.locked ? 'var(--muted-foreground)' : 'var(--foreground)',
          opacity: isDimmed ? 0.86 : 1,
          cursor: isInteractive ? 'pointer' : 'not-allowed',
          boxShadow: isSelected ? 'var(--elevation-sm)' : 'none',
        }}
      >
        <div className="tw-flex tw-items-center tw-min-w-0 tw-flex-1" style={{ gap: 'var(--spacing-3)' }}>
          <ChevronDown
            style={{
              width: '14px',
              height: '14px',
              transition: 'transform 0.2s ease',
              transform: isSelected ? 'rotate(0deg)' : 'rotate(-90deg)',
              opacity: section.locked ? 0.5 : 0.95,
              flexShrink: 0,
            }}
          />
          <span className="tw-min-w-0">{section.label}</span>
        </div>
        {inlineSupportText ? (
          <span
            style={{
              color: 'var(--muted-foreground)',
              fontSize: '12px',
              lineHeight: 1.2,
              opacity: 0.9,
              marginLeft: 'auto',
              textAlign: 'right',
            }}
          >
            {inlineSupportText}
          </span>
        ) : null}
        <div className="tw-ml-2 tw-flex tw-items-center">
          {section.locked ? (
            <Lock style={{ width: '16px', height: '16px', opacity: 0.8, flexShrink: 0 }} />
          ) : section.completed ? (
            <span
              aria-label={`${section.id} complete`}
              title="Completed"
              className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-green-100 tw-text-green-700"
              style={{ width: '18px', height: '18px', flexShrink: 0 }}
            >
              <Check style={{ width: '12px', height: '12px' }} />
            </span>
          ) : null}
        </div>
      </button>
    );
  };

  return (
    <Panel
      className={`tw-h-full tw-shrink-0 ${className ?? ''}`.trim()}
      style={{
        width: resolvedWidth,
        minWidth: resolvedWidth,
        maxWidth: resolvedWidth,
      }}
    >
      <div className="panel-scrollbar tw-flex tw-h-full tw-min-h-0 tw-flex-col tw-overflow-y-auto tw-overflow-x-hidden">
        {header}
        <div
          className="tw-flex tw-flex-col"
          style={{
            gap: 'var(--spacing-2)',
            padding: 'var(--spacing-3)',
            flex: 1,
            minHeight: 0,
          }}
        >
          {sections.map(section => {
            const isSelected = selectedSectionId === section.id;
            const showSupportText = Boolean(section.supportText);
            const trigger = renderSectionTrigger(
              section,
              isSelected,
              showSupportText ? section.supportText : undefined,
            );

            return (
              <div
                key={section.id}
                className="tw-flex tw-flex-col"
                style={{
                  flex: isSelected ? '1 1 auto' : '0 0 auto',
                  minHeight: isSelected ? `${MIN_EXPANDED_SECTION_HEIGHT}px` : undefined,
                  minWidth: 0,
                }}
              >
                {trigger}
                <div
                  className="tw-overflow-hidden tw-transition-all tw-duration-300 tw-ease-out"
                  style={{
                    flex: isSelected ? '1 1 auto' : '0 0 auto',
                    minHeight: 0,
                    opacity: isSelected ? 1 : 0,
                    marginTop: isSelected ? 'var(--spacing-2)' : '0px',
                    marginBottom: isSelected ? 'var(--spacing-1)' : '0px',
                    transform: isSelected ? 'translateY(0px)' : 'translateY(-6px)',
                  }}
                  aria-hidden={!isSelected}
                >
                  {isSelected ? (
                    <div
                      style={{
                        height: '100%',
                        minHeight: 0,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        border: '1px solid var(--primary)',
                        borderTop: 'none',
                        borderBottomLeftRadius: 'var(--radius)',
                        borderBottomRightRadius: 'var(--radius)',
                        padding: 'var(--spacing-2)',
                        background: 'var(--card)',
                      }}
                    >
                      {section.innerNav}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Panel>
  );
};

export default SectionSidebar;
